import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { Box, Button, FormControlLabel, Grid, Switch } from '@mui/material'
import { useEffect, useState } from 'react'
import { useInput } from '../../hooks/useInput'
import { PromoCodeMethod, modifyPromoCode } from '../../modules/promoCodeModify'
import { useAppDispatch, useAppSelector } from '../../store'
import Datepicker from '../Form/Datepicker'
import FormContainer from '../Form/FormContainer'
import TextInput from '../Form/TextInput'
import Message from '../Message/Message'
import ModalPopUp from '../ModalPopUp/ModalPopUp'

interface Props {
  promoCode?: PromoCode
  setPromoCode: (promoCode: PromoCode | undefined) => void
  setShowModal: (show: boolean) => void
  showModal: boolean
}

const AddEditPromoCodes = ({
  promoCode,
  setPromoCode,
  setShowModal,
  showModal,
}: Props) => {
  const dispatch = useAppDispatch()
  const { error, loading } = useAppSelector((state) => state.promoCodeCreate)

  const { value: description, bind: bindDescription } = useInput(
    promoCode?.description
  )
  const {
    value: name,
    bind: bindName,
    setError: setNameError,
    error: nameError,
  } = useInput(promoCode?.name)
  const {
    bind: bindValue,
    value,
    setValue,
    setError: setValueError,
    error: valueError,
  } = useInput(String(promoCode?.value))

  const [active, setActive] = useState(promoCode ? promoCode.active : true)
  const [expires, setExpires] = useState(promoCode ? promoCode.expires : false)

  const [start, setStart] = useState<MaterialUiPickersDate | null>(
    promoCode && promoCode.start ? promoCode.start : null
  )
  const [end, setEnd] = useState<MaterialUiPickersDate | null>(
    promoCode && promoCode.end ? promoCode.end : null
  )

  useEffect(() => {
    if (Number(value) > 100) {
      setValue('100')
    }
  }, [value])

  const handleCancel = () => {
    setPromoCode(undefined)
    setShowModal(false)
  }

  const handleSubmit = () => {
    let ready = true
    if (!name) {
      setNameError('Required')
      ready = false
    }

    if (!value) {
      setValueError('Required')
      ready = false
    }

    const updatedPromoCode = {
      ...promoCode,
      name: String(name),
      description: description ? String(description) : undefined,
      value: Number(value),
      active,
      expires,
      start: expires ? (start as Date) : undefined,
      end: expires ? (end as Date) : undefined,
    }

    if (ready) {
      dispatch(
        modifyPromoCode(
          updatedPromoCode,
          promoCode ? PromoCodeMethod.UPDATE : PromoCodeMethod.CREATE
        )
      )
    }
  }

  return (
    <ModalPopUp
      title={promoCode ? 'Edit promo code' : 'Add promo code'}
      setShowModal={handleCancel}
      showModal={showModal}
      variant="small"
    >
      <FormContainer style={{ padding: 0, minHeight: 360 }}>
        <h4 className="m-b-30">
          {promoCode ? 'Edit promo code' : 'Add promo code'}
        </h4>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput
              name="name"
              label="Name"
              bindValue={bindName}
              error={nameError}
            />
          </Grid>

          <Grid item xs={6}>
            <TextInput
              type="number"
              name="value"
              label="Percentage value"
              bindValue={bindValue}
              error={valueError}
            />
          </Grid>

          <Grid item xs={12}>
            <TextInput
              name="description"
              label="Description"
              autoComplete="off"
              bindValue={bindDescription}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={expires}
                  onChange={() => setExpires(!expires)}
                />
              }
              label="Expires"
            />
          </Grid>

          {expires && (
            <>
              <Grid item xs={6}>
                <p className="fs12 mb-1">Start Date</p>
                <Datepicker
                  value={start}
                  setValue={setStart}
                  className="m-t-0 m-b-0"
                  disablePast={false}
                  disableFuture={false}
                />
              </Grid>
              <Grid item xs={6}>
                <p className="fs12 mb-1">End Date</p>
                <Datepicker
                  value={end}
                  setValue={setEnd}
                  className="m-t-0 m-b-0"
                  disablePast={false}
                  disableFuture={false}
                />
              </Grid>
            </>
          )}
        </Grid>

        {error && <Message variant="error">{error}</Message>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6 }}>
          <Button
            color="primary"
            variant="outlined"
            className="m-r-24"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Box>
      </FormContainer>
    </ModalPopUp>
  )
}

export default AddEditPromoCodes
